import dayjs from 'dayjs'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { UserPopover } from '~/components/account/UserPopover'
import { Block } from '~/components/blocks/Block'
import { Block as BlockType } from '~/components/chat/message'
import { Button } from '~/components/common/Button'
import { Input } from '~/components/common/CurieInput'
import { HoverText } from '~/components/common/HoverText'
import { ProgressSpinner } from '~/components/common/ProgressSpinner'
import ProgressMarkdown from '~/components/common/ProgressMarkdown'
import { AzureDeployTaskModal } from '~/components/migration/AzureDeployTaskModal'
import { MigrationEndpointSelector } from '~/components/migration/MigrationEndpointSelector'
import { TaskStatusDetailModalProps } from '~/components/modals/TaskStatusDetailsModal'
import { TodoList } from '~/components/task/TodoList'
import { AgentCommands, UsedCommandType } from '~/constants/commands'
import { TaskStatusDetailType, TaskStatuses } from '~/constants/task-status'
import { useTask, useTaskContext } from '~/contexts/TaskContext'
import { useConversationUuid } from '~/hooks'
import { useRepoContexts } from '~/hooks/task/useRepoContexts'
import type { ProgressMessage } from '~/hooks/task/useTaskProgressMessages'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { ModalId, useModalController } from '~/hooks/useModal'
import {
  AgentTaskStatus,
  agentTaskStatusFromJSON,
  CommandType,
  commandTypeFromJSON,
  MigrationSelectedEndpoint,
  ProgressMessageType,
  TaskStatusAndRequestResponseTuple
} from '~/protos/agents'
import {
  regenerateImage,
  submitMigrationEndpoints,
  submitQuestionAnswer
} from '~/services/taskService'
import { MigrationEndpoint } from '~/types/migration'
import { isTaskEffectivelyDone } from '~/task/commandTypeUtils'
import { extractFileDisplayName } from '~/utils/repositoryUtils'
import { toCamelCase } from '~/utils/shared'
import { BetterToast } from '~/utils/toast'
import { verifyUUID } from '~/utils/utils'

type TaskProgressStep = {
  key: string
  statusLabel: string
  statusValue?: string
  messages: ProgressMessage[]
  timestamp?: number
  index: number
  status: AgentTaskStatus
  /** User feedback text (for GENERAL_TXT_FEEDBACK_PROVIDED status) */
  feedbackText?: string
}

/** Info extracted from a tuple for display */
interface TupleStatusInfo {
  status: AgentTaskStatus
  statusKey: string
  label: string
  timestamp?: number
  index: number
  /** User feedback text (for GENERAL_TXT_FEEDBACK_PROVIDED status) */
  feedbackText?: string
}

function hasRiskReviewFeedback(tuple: TaskStatusAndRequestResponseTuple): boolean {
  const feedback = tuple.request?.migrationRiskReviewFeedback
  if (!feedback) return false
  return feedback.feedback.trim().length > 0 || feedback.uploadedFiles.length > 0
}

// Helper to detect if a string looks like a file name (same heuristic as ProgressMessageView)
function isFileName(msg: string): boolean {
  const trimmed = msg.trim()
  return /^[\w\-./]+\.\w+$/.test(trimmed) || /^[\w\-./\\]+$/.test(trimmed)
}

function Chevron({ isExpanded }: { isExpanded: boolean }) {
  return (
    <div className='w-4 h-4 flex items-center justify-center'>
      <svg
        width='16'
        height='16'
        viewBox='0 0 16 16'
        fill='none'
        className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : 'rotate-0'}`}
      >
        <path
          d='M11.354 8.35403L6.35403 13.354C6.30757 13.4005 6.25242 13.4373 6.19173 13.4625C6.13103 13.4876 6.06598 13.5006 6.00028 13.5006C5.93458 13.5006 5.86953 13.4876 5.80883 13.4625C5.74813 13.4373 5.69298 13.4005 5.64653 13.354C5.60007 13.3076 5.56322 13.2524 5.53808 13.1917C5.51294 13.131 5.5 13.066 5.5 13.0003C5.5 12.9346 5.51294 12.8695 5.53808 12.8088C5.56322 12.7481 5.60007 12.693 5.64653 12.6465L10.2934 8.00028L5.64653 3.35403C5.55271 3.26021 5.5 3.13296 5.5 3.00028C5.5 2.8676 5.55271 2.74035 5.64653 2.64653C5.74035 2.55271 5.8676 2.5 6.00028 2.5C6.13296 2.5 6.26021 2.55271 6.35403 2.64653L11.354 7.64653C11.4005 7.69296 11.4374 7.74811 11.4626 7.80881C11.4877 7.86951 11.5007 7.93457 11.5007 8.00028C11.5007 8.06599 11.4877 8.13105 11.4626 8.19175C11.4374 8.25245 11.4005 8.30759 11.354 8.35403Z'
          fill='rgba(0,0,0,0.55)'
        />
      </svg>
    </div>
  )
}

function StatusIcon({ kind }: { kind: 'done' | 'active' }) {
  if (kind === 'done') {
    return (
      <div className='w-5 h-5 flex items-center justify-center rounded-full bg-[#846FE1]'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
        >
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M13.3334 4L6.00008 11.3333L2.66675 8'
            stroke='white'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </div>
    )
  }
  return (
    <div className='w-5 h-5 flex items-center justify-center'>
      <ProgressSpinner size='lg' />
    </div>
  )
}

function ClockMetaIcon() {
  return (
    <div className='w-[16px] h-[16px] fill-[rgba(0,0,0,0.25)] aspect-square self-center'>
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='13.333'
        height='13.333'
        viewBox='0 0 13.333 13.333'
        fill='none'
      >
        <path
          d='M6.66667 1.33333C3.72115 1.33333 1.33333 3.72115 1.33333 6.66667C1.33333 9.61218 3.72115 12 6.66667 12C9.61218 12 12 9.61218 12 6.66667C12 3.72115 9.61218 1.33333 6.66667 1.33333ZM0 6.66667C0 2.98477 2.98477 0 6.66667 0C10.3486 0 13.3333 2.98477 13.3333 6.66667C13.3333 10.3486 10.3486 13.3333 6.66667 13.3333C2.98477 13.3333 0 10.3486 0 6.66667ZM6 2.66667H7.33333V6.25464L9.63147 7.40371L9.03519 8.59628L6 7.07869V2.66667Z'
          fill='black'
          fillOpacity='0.25'
        />
      </svg>
    </div>
  )
}

interface TimelineConnectorProps {
  bottom: string
  background?: string
  solidColor?: string
}

function TimelineConnector({ bottom, background, solidColor }: TimelineConnectorProps) {
  return (
    <div
      className={`absolute left-[11px] top-[38px] w-[2px] ${solidColor || ''}`}
      style={{ bottom, ...(background ? { background } : {}) }}
    />
  )
}

interface InlineActionButtonProps {
  label: string
  onClick: () => void
  stopPropagation?: boolean
  /** 'navigate' = prominent CTA (redirect to another view), 'detail' = subtle secondary (open inline panel) */
  variant?: 'navigate' | 'detail'
}

function InlineActionButton({
  label,
  onClick,
  stopPropagation = false,
  variant = 'detail'
}: InlineActionButtonProps) {
  const handleClick = (e: React.MouseEvent) => {
    if (stopPropagation) e.stopPropagation()
    onClick()
  }

  if (variant === 'navigate') {
    return (
      <button
        type='button'
        className='flex items-center justify-center self-stretch flex-[1_0_0]
                    h-[36px] gap-[8px] px-[14px] py-[8px] rounded-[12px]
                    text-[12px] text-center font-sans font-medium leading-normal text-[#5B5BD5]
                    bg-[rgba(132,111,225,0.10)] hover:bg-[rgba(132,111,225,0.20)] transition-colors select-none'
        onClick={handleClick}
      >
        <div>{label}</div>
        <div className='w-[16px] h-[16px]'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 16 16'
            fill='none'
          >
            <path
              d='M5.33354 13.8334C5.46626 13.834 5.59362 13.7812 5.68688 13.6867L11.0202 8.35339C11.2152 8.15818 11.2152 7.84194 11.0202 7.64673L5.68688 2.31339C5.48987 2.12982 5.18287 2.13524 4.99246 2.32565C4.80206 2.51605 4.79664 2.82305 4.98021 3.02006L9.96021 8.00006L4.98021 12.9801C4.78524 13.1753 4.78524 13.4915 4.98021 13.6867C5.07347 13.7812 5.20083 13.834 5.33354 13.8334Z'
              fill='#5B5BD5'
            />
          </svg>
        </div>
      </button>
    )
  }

  return (
    <button
      type='button'
      className='flex items-center justify-center self-stretch px-[16px] py-[8px] gap-[8px] flex-[1_1_0] select-none cursor-pointer text-tokens-neutral-text-tertiary hover:text-tokens-neutral-text-secondary transition-colors'
      onClick={handleClick}
    >
      <svg width='14' height='14' viewBox='0 0 16 16' fill='none'>
        <path
          d='M9.33366 1.3335H4.00033C3.6467 1.3335 3.30756 1.47397 3.05752 1.72402C2.80747 1.97407 2.66699 2.31321 2.66699 2.66683V13.3335C2.66699 13.6871 2.80747 14.0263 3.05752 14.2763C3.30756 14.5264 3.6467 14.6668 4.00033 14.6668H12.0003C12.3539 14.6668 12.6931 14.5264 12.9431 14.2763C13.1932 14.0263 13.3337 13.6871 13.3337 13.3335V5.3335L9.33366 1.3335Z'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M9.33301 1.3335V5.3335H13.333'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M10.6663 8.6665H5.33301'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M10.6663 11.3335H5.33301'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M6.66634 6H5.99967H5.33301'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
      <span className='text-[12px] font-medium font-sans leading-normal'>{label}</span>
    </button>
  )
}

function getActionLabel(
  status: AgentTaskStatus,
  isDocumentCommand: boolean,
  commandType?: CommandType
): string {
  switch (status) {
    case AgentTaskStatus.DIFF_GENERATED:
      return isDocumentCommand ? 'View Result' : 'View Diff'
    case AgentTaskStatus.DRAFT_GENERATED:
      return 'View Draft'
    case AgentTaskStatus.HUMAN_DIFF_UPDATED:
    case AgentTaskStatus.DIFF_COMMENT_ADDED:
      return 'View Diff'
    case AgentTaskStatus.PLANNING_UPDATED:
      return 'View Plan'
    case AgentTaskStatus.DOCUMENT_GENERATED:
      if (commandType !== undefined && dataWeaveCommandTypes.includes(commandType))
        return 'View DataWeave'
      if (commandType === CommandType.MUNIT_TEST_CREATION) return 'View MUnit Tests'
      return 'View Document'
    case AgentTaskStatus.DOCUMENT_UPDATED:
    case AgentTaskStatus.MIGRATION_DOCUMENT_GENERATED:
      return 'View Document'
    case AgentTaskStatus.STEPS_GENERATED:
    case AgentTaskStatus.STEPS_UPDATED:
      return 'View Steps'
    case AgentTaskStatus.MUNIT_SCENARIO_GENERATED:
      return 'View Scenario'
    case AgentTaskStatus.MIGRATION_API_SPEC_GENERATED:
    case AgentTaskStatus.REFACTOR_API_PROPOSED:
      return 'View API Spec'
    case AgentTaskStatus.MIGRATION_CODE_GENERATED:
    case AgentTaskStatus.REFACTOR_CODE_GENERATED:
      return 'View Code'
    case AgentTaskStatus.MIGRATION_ENDPOINTS_SELECTED:
      return 'View Endpoints'
    case AgentTaskStatus.IMAGE_GENERATED:
      return 'View Image'
    case AgentTaskStatus.SUBMITTED:
      return 'View Details'
    default:
      return 'View Details'
  }
}

interface StatusLabelRowProps {
  label: string
  expandable: boolean
  isExpanded: boolean
  onToggle: () => void
  /** User feedback text to show in gray after the label */
  feedbackText?: string
  /** If false, uses intrinsic width instead of stretching to full width */
  fullWidth?: boolean
}

interface TaskHeaderFieldProps {
  label: string
  value: React.ReactNode
}

function StatusLabelRow({
  label,
  expandable,
  isExpanded,
  onToggle,
  feedbackText,
  fullWidth = true
}: StatusLabelRowProps) {
  const labelElement = (
    <div className='flex flex-col justify-center items-start'>
      <div className='text-[#846FE1] text-center items-baseline gap-2 text-[14px] leading-[22px]'>
        <span className='font-medium'>{label}</span>
        {feedbackText && (
          <span className='font-medium text-[rgba(0,0,0,0.5)] truncate'>
            {'\u201C'}
            {feedbackText}
            {'\u201D'}
          </span>
        )}
      </div>
    </div>
  )

  if (!expandable) return labelElement

  return (
    <button
      type='button'
      className={`flex items-center gap-[8px] ${fullWidth ? 'w-full' : ''}`}
      onClick={onToggle}
    >
      {labelElement}
      <div className='flex items-center justify-center w-4 h-4'>
        <Chevron isExpanded={isExpanded} />
      </div>
    </button>
  )
}

function TaskHeaderField({
  label,
  value,
  allowOverflow = false
}: TaskHeaderFieldProps & { allowOverflow?: boolean }) {
  return (
    <div className='flex flex-col gap-[4px] flex-[1_0_0]'>
      <div className='text-[11px] font-semibold font-mono text-[rgba(0,0,0,0.45)] tracking-[-0.33px] leading-normal line-clamp-1 overflow-ellipsis'>
        {label}
      </div>
      <div
        className={`text-[14px] font-medium font-sans text-[rgba(0,0,0,0.65)] leading-[22px] ${allowOverflow ? '' : 'line-clamp-1 overflow-ellipsis'}`}
      >
        {value}
      </div>
    </div>
  )
}

function RepositoryListDisplay({ names }: { names: string[] }) {
  return (
    <div className='relative inline-flex items-center gap-1.5'>
      <span>{names.length} repositories</span>
      <div className='group/repo'>
        <div
          className='inline-flex items-center justify-center w-[16px] h-[16px] rounded-full border border-[rgba(0,0,0,0.2)] text-[10px] font-semibold text-[rgba(0,0,0,0.4)] hover:bg-[rgba(0,0,0,0.06)] hover:text-[rgba(0,0,0,0.6)] transition-colors cursor-default leading-none'
          aria-label='View repository list'
        >
          i
        </div>
        <div className='absolute top-full left-0 mt-1 z-50 min-w-[200px] max-w-[360px] rounded-lg border border-[rgba(0,0,0,0.08)] bg-white shadow-lg py-2 px-3 hidden group-hover/repo:block group-hover/repo:animate-enter origin-top-left'>
          <div className='text-[10px] font-semibold text-[rgba(0,0,0,0.35)] uppercase tracking-wide'>
            Repositories
          </div>
          <ul className='flex flex-col gap-1'>
            {names.map(name => (
              <li
                key={name}
                className='text-[13px] text-[rgba(0,0,0,0.65)] leading-normal font-medium truncate'
              >
                {name}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

/**
 * Inline display of a scheduled_task uuid in the task info header.
 * Shows a short truncated form (matches the Settings > Scheduled Tasks
 * table); clicking copies the full uuid to the clipboard. Falls back to
 * `Schedule #<integer-id>` if the uuid somehow didn't come through
 * (older records, transient lookup failure) so the field is never empty.
 */
function ScheduleUuidBadge({ uuid, fallbackId }: { uuid: string | null; fallbackId: number }) {
  if (!uuid) {
    return <span>{`Schedule #${fallbackId}`}</span>
  }
  const short = uuid.length > 12 ? `${uuid.slice(0, 8)}…${uuid.slice(-4)}` : uuid
  return (
    <HoverText content={uuid} placement='top'>
      <button
        type='button'
        className='font-mono underline decoration-dotted underline-offset-2 hover:text-[rgba(0,0,0,0.85)] text-left leading-[22px]'
        onClick={() => {
          void navigator.clipboard.writeText(uuid).then(
            () => BetterToast.success('Schedule ID copied'),
            () => BetterToast.error('Failed to copy ID')
          )
        }}
      >
        {short}
      </button>
    </HoverText>
  )
}

function CheckpointCard({ text }: { text: string }) {
  return (
    <div className='rounded-[18px] bg-[rgba(0,0,0,0.03)] px-6 py-4'>
      <div className='inline-flex items-center rounded-full bg-[rgba(91,91,213,0.12)] px-3 py-1 text-[10px] font-semibold tracking-wide text-[rgba(91,91,213,1)]'>
        CHECK POINT
      </div>
      <div className='mt-3 text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)]'>{text}</div>
    </div>
  )
}

/** DataWeave command types — generate DataWeave code, not generic "documents" */
const dataWeaveCommandTypes: CommandType[] = [
  CommandType.GENERATE_DATAWEAVE,
  CommandType.DATAWEAVE_TO_DATA_CREATION,
  CommandType.GENERATE_DATAWEAVE_REPO_AWARE,
  CommandType.DATAWEAVE_TO_DATA_CREATION_REPO_AWARE,
  CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION,
  CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION_REPO_AWARE
]

/** Resolve a human-readable label for DOCUMENT_GENERATED based on command type */
function resolveDocumentGeneratedLabel(commandType?: CommandType): string {
  if (commandType !== undefined && dataWeaveCommandTypes.includes(commandType)) {
    return 'DataWeave Generated'
  }
  if (commandType === CommandType.MUNIT_TEST_CREATION) {
    return 'MUnit Tests Generated'
  }
  return 'Document Generated'
}

/** Extract status info from tuples */
function extractStatusesFromTuples(
  tuples: TaskStatusAndRequestResponseTuple[],
  isDocumentCommand = false,
  commandType?: CommandType
): TupleStatusInfo[] {
  const result: TupleStatusInfo[] = []
  for (let i = 0; i < tuples.length; i++) {
    const tuple = tuples[i]
    const status = tuple.taskStatus
    if (status === undefined || status === AgentTaskStatus.NONE_TASK_STATUS) continue

    const statusKey = AgentTaskStatus[status] ?? String(status)
    // createdTsSec is in seconds, convert to milliseconds for dayjs
    const timestamp = tuple.createdTsSec ? tuple.createdTsSec * 1000 : undefined

    // Extract feedback text for feedback statuses
    // The feedback is stored in command.commandString when regenerating images
    let feedbackText: string | undefined
    if (status === AgentTaskStatus.GENERAL_TXT_FEEDBACK_PROVIDED) {
      feedbackText = tuple.request?.command?.commandString || tuple.request?.generalDiffComment
    }

    // For feedback status, use "Feedback Sent" as the label
    // Use custom label from TaskStatuses if defined, otherwise fall back to toCamelCase
    // For document commands (e.g. ITA), override labels: DIFF_GENERATED → "Results Generated", HUMAN_DIFF_UPDATED → "Results approved"
    let resolvedLabel = TaskStatuses[status]?.label || toCamelCase(statusKey)
    if (isDocumentCommand && status === AgentTaskStatus.DIFF_GENERATED) {
      resolvedLabel = 'Results Generated'
    }
    if (isDocumentCommand && status === AgentTaskStatus.HUMAN_DIFF_UPDATED) {
      resolvedLabel = 'Results Approved'
    }
    // Override DOCUMENT_GENERATED label based on specific command type
    if (status === AgentTaskStatus.DOCUMENT_GENERATED) {
      resolvedLabel = resolveDocumentGeneratedLabel(commandType)
    }
    if (status === AgentTaskStatus.MIGRATION_RISKS_REVIEWED) {
      resolvedLabel = hasRiskReviewFeedback(tuple)
        ? 'User provided feedback'
        : 'User skipped feedback'
    }
    const label = feedbackText ? 'Feedback Sent: ' : resolvedLabel

    result.push({
      status,
      statusKey,
      label,
      timestamp,
      index: i,
      feedbackText
    })
  }
  return result
}

function groupMessagesIntoStatusSteps(
  messages: ProgressMessage[],
  tupleStatuses: TupleStatusInfo[],
  isDocumentCommand = false,
  commandType?: CommandType
): TaskProgressStep[] {
  const sorted = [...messages].sort((a, b) => (a.createdTsMs ?? 0) - (b.createdTsMs ?? 0))

  // Build a map of status -> messages
  const byStatus = new Map<string, ProgressMessage[]>()
  const unknown: ProgressMessage[] = []

  for (const msg of sorted) {
    if (!msg) continue
    if (msg.messageType === ProgressMessageType.SUMMARY) continue
    // Skip TODO_LIST messages - they are rendered separately when needed
    if (msg.messageType === ProgressMessageType.TODO_LIST) continue

    const status = msg.taskStatus ?? ''
    if (!status) {
      unknown.push(msg)
      continue
    }

    if (!byStatus.has(status)) {
      byStatus.set(status, [])
    }
    byStatus.get(status)!.push(msg)
  }

  const steps: TaskProgressStep[] = []

  // Include unknown-status messages first (rare but possible).
  if (unknown.length > 0) {
    steps.push({
      key: 'status-unknown',
      statusLabel: 'Progress',
      statusValue: undefined,
      messages: unknown,
      index: -1,
      status: AgentTaskStatus.NONE_TASK_STATUS
    })
  }

  // Use tuple statuses as the canonical order (they represent the actual state transitions)
  // Each tuple is a separate step - don't deduplicate by status, use tuple index for uniqueness
  // This allows multiple IMAGE_GENERATED, GENERAL_TXT_FEEDBACK_PROVIDED, etc.
  const usedMessageIndices = new Set<number>()

  for (const tupleStatus of tupleStatuses) {
    // Get messages for this status that haven't been used yet
    const allMsgsForStatus = byStatus.get(tupleStatus.statusKey) ?? []
    const msgs: ProgressMessage[] = []

    // Assign all unused messages for this status to the first tuple that claims them.
    // Subsequent tuples with the same status will get an empty list (messages already used).
    for (const msg of allMsgsForStatus) {
      const msgIndex = sorted.indexOf(msg)
      if (!usedMessageIndices.has(msgIndex)) {
        msgs.push(msg)
        usedMessageIndices.add(msgIndex)
      }
    }

    // Use tuple timestamp, or fallback to first message timestamp
    const timestamp = tupleStatus.timestamp || (msgs.length > 0 ? msgs[0].createdTsMs : undefined)

    // Use tuple index in the key to make each step unique
    steps.push({
      key: `${tupleStatus.statusKey}-${tupleStatus.index}`,
      statusLabel: tupleStatus.label,
      statusValue: tupleStatus.statusKey,
      messages: msgs,
      timestamp,
      index: tupleStatus.index,
      status: tupleStatus.status,
      feedbackText: tupleStatus.feedbackText
    })
  }

  // Also include any statuses from messages that weren't in tuples (edge case)
  // Build set of statuses that have been added from tuples
  const tupleStatusKeys = new Set(tupleStatuses.map(t => t.statusKey))
  for (const [status, msgs] of byStatus) {
    if (!tupleStatusKeys.has(status)) {
      // Try to parse status as AgentTaskStatus
      const parsedStatus =
        AgentTaskStatus[status as keyof typeof AgentTaskStatus] ?? AgentTaskStatus.NONE_TASK_STATUS
      let fallbackLabel = TaskStatuses[parsedStatus]?.label || toCamelCase(status)
      if (isDocumentCommand && parsedStatus === AgentTaskStatus.DIFF_GENERATED) {
        fallbackLabel = 'Results Generated'
      }
      if (parsedStatus === AgentTaskStatus.DOCUMENT_GENERATED) {
        fallbackLabel = resolveDocumentGeneratedLabel(commandType)
      }
      steps.push({
        key: status,
        statusLabel: fallbackLabel,
        statusValue: status,
        messages: msgs,
        index: -1,
        status: parsedStatus
      })
    }
  }

  return steps
}

// Accordion state is kept in component state only (not persisted)
// Each task gets its own fresh state, defaulting to all isExpanded

/** Timeline block with fields and children */
interface TimelineBlockData {
  id?: string
  fields?: Record<string, unknown>
  children?: unknown[] | null
}

interface TaskProgressMessageViewProps {
  progressMessages: ProgressMessage[]
  taskStatus?: AgentTaskStatus
  /** Task status tuples - used to show all statuses even without progress messages */
  tuples?: TaskStatusAndRequestResponseTuple[]
  /** Timeline blocks from taskBlock.children - source of user attribution and child blocks */
  timelineBlocks?: TimelineBlockData[]
  /** Callback to navigate to Review Code / View Results tab */
  onNavigateToReviewCode?: () => void
  /** Callback to navigate to the Docs / Results tab for a generated document
   *  step. When provided, "View Document" steps redirect into the existing
   *  doc viewer tab instead of opening a heavy modal that re-renders the
   *  full doc content inline. Receives the step status so the caller can
   *  pick the correct destination tab. */
  onNavigateToDocument?: (status: AgentTaskStatus, stepIndex: number, stepLabel: string) => void
  /** Whether this is a document-generating command (e.g. Integration Test) */
  isDocumentCommand?: boolean
  /** The command type — used to resolve context-specific labels (e.g. DataWeave vs Document) */
  commandType?: CommandType
}

/**
 * Task-page progress view.
 * Renders DB-backed progress messages as a list of status groups.
 * Keeps the underlying message semantics, but uses task-page styling/layout.
 * Shows all task statuses from tuples, with progress message details where available.
 */
/** Statuses whose "View …" CTA renders a (potentially huge) generated
 *  document. These are the ones that should redirect into the dedicated doc
 *  viewer tab rather than render inside a modal. */
const DOCUMENT_REDIRECT_STATUSES: ReadonlySet<AgentTaskStatus> = new Set([
  AgentTaskStatus.DOCUMENT_GENERATED,
  AgentTaskStatus.DOCUMENT_UPDATED,
  AgentTaskStatus.MIGRATION_DOCUMENT_GENERATED
])

export function TaskProgressMessageView({
  progressMessages,
  taskStatus,
  tuples = [],
  timelineBlocks = [],
  onNavigateToReviewCode,
  onNavigateToDocument,
  isDocumentCommand = false,
  commandType
}: TaskProgressMessageViewProps) {
  const isPlugin = useCompactViewport()
  const { openModal } = useModalController<TaskStatusDetailModalProps>(ModalId.TaskStatusDetails)
  const { workspaceUuid, uuid } = useParams<{ workspaceUuid: string; uuid: string }>()
  const { task, taskId } = useTask()
  const { refetch: refetchTask } = useTaskContext()
  const url = useConversationUuid()
  const isAdminTask = !verifyUUID(url)

  // Image feedback state (for IMAGE_GENERATED status)
  const [imageFeedback, setImageFeedback] = useState('')
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false)

  // Endpoint selection state (for MIGRATION_ENDPOINTS_IDENTIFIED status)
  const [selectedEndpoints, setSelectedEndpoints] = useState<MigrationEndpoint[]>([])
  const [isSubmittingEndpoints, setIsSubmittingEndpoints] = useState(false)
  const [hasInitializedEndpoints, setHasInitializedEndpoints] = useState(false)

  // Question answer state (for QUESTION_ASKED status)
  const [questionAnswer, setQuestionAnswer] = useState('')
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false)

  // Accordion isExpanded state - defaults to all isExpanded (via ?? true in render)
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({})

  // Auto-popup the risk-review modal whenever the active task is parked in
  // MIGRATION_AWAITING_RISK_REVIEW. We want the popup to fire:
  //   1. On initial load if the task is already in the state.
  //   2. When the same task transitions from any other status into the state
  //      (e.g. agent finishes regenerating and re-parks for another review
  //      round).
  //   3. When the user navigates between tasks and the *new* task happens to
  //      be in the state — the prop value of `taskStatus` doesn't change in
  //      that case (still MIGRATION_AWAITING_RISK_REVIEW), so we MUST also
  //      key the guard on `taskId` to detect the task swap.
  //
  // The popup must NOT re-fire on every render of the same task while it
  // sits in the state — if the user closes the modal we keep it closed
  // until one of the conditions above flips. We track that with a ref
  // holding the `${taskId}:${taskStatus}` we last auto-opened for, and
  // clear it the moment the task is no longer in the awaiting state.
  const autoOpenedRiskReviewKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (taskStatus !== AgentTaskStatus.MIGRATION_AWAITING_RISK_REVIEW) {
      autoOpenedRiskReviewKeyRef.current = null
      return
    }
    if (taskId === undefined || taskId === null) return

    const key = `${taskId}:${taskStatus}`
    if (autoOpenedRiskReviewKeyRef.current === key) return

    let riskReviewIndex = -1
    let riskReviewLabel = TaskStatuses[taskStatus]?.label ?? 'Review risks'
    for (let i = tuples.length - 1; i >= 0; i--) {
      if (tuples[i].taskStatus === AgentTaskStatus.MIGRATION_AWAITING_RISK_REVIEW) {
        riskReviewIndex = i
        break
      }
    }
    if (riskReviewIndex === -1) return
    if (timelineBlocks.length === 0) return

    const matchingBlock = timelineBlocks.find(
      block =>
        typeof block.fields?.index === 'number' &&
        block.fields.index === riskReviewIndex &&
        typeof block.fields?.desc === 'string'
    )
    if (matchingBlock?.fields?.desc) {
      riskReviewLabel = matchingBlock.fields.desc as string
    }

    autoOpenedRiskReviewKeyRef.current = key
    // Defer the openModal event past the current commit's effect cycle.
    // `CommonModal` for `ModalId.TaskStatusDetails` is mounted as a *later*
    // sibling of `TaskDisplay` in `pages/index.tsx`, which means its
    // `useEffect` that subscribes to the `OpenModal` event runs AFTER our
    // effect here on initial mount (React fires effects bottom-up in tree
    // order). If we emitted synchronously, the event would be lost — the
    // listener doesn't exist yet — and the modal would silently fail to
    // open. A single `setTimeout(0)` queues the emit as a fresh macrotask
    // after the current commit's effect cycle, so all sibling listeners
    // are guaranteed to be registered by the time the event fires. We
    // capture the timer id and clear it on cleanup so a fast unmount
    // (e.g. user navigates away mid-frame) doesn't open a stale modal.
    const timerId = window.setTimeout(() => {
      openModal({ title: riskReviewLabel, taskStatus, index: riskReviewIndex })
    }, 0)
    return () => window.clearTimeout(timerId)
  }, [taskId, taskStatus, tuples, timelineBlocks, openModal])

  // The deploy panel renders inline in the timeline (see below), so unlike the
  // risk-review pause it does not need to auto-open as a modal.

  // Extract endpoints from the MIGRATION_ENDPOINTS_IDENTIFIED tuple
  const migrationEndpoints = useMemo(() => {
    for (const tuple of tuples) {
      if (tuple.taskStatus === AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED) {
        const identifiedEndpoints = tuple.response?.[0]?.responseContentStream?.identifiedEndpoints
        const jsonMetadata = tuple.response?.[0]?.responseContentStream?.jsonMetadata

        if (identifiedEndpoints && identifiedEndpoints.length > 0) {
          // New format: use identifiedEndpoints directly
          return identifiedEndpoints.map(endpoint => ({
            name: endpoint.name,
            category: endpoint.category,
            file_loc: endpoint.filePath
          })) as MigrationEndpoint[]
        } else if (jsonMetadata) {
          // Legacy fallback: parse from jsonMetadata
          try {
            const parsedMetadata = JSON.parse(jsonMetadata)
            const migrationObj = parsedMetadata.mig_obj ? JSON.parse(parsedMetadata.mig_obj) : null
            if (migrationObj?.endpoints) {
              return migrationObj.endpoints.map(
                (endpoint: { name: string; category: string; file_loc: string }) => ({
                  name: endpoint.name,
                  category: endpoint.category,
                  file_loc: endpoint.file_loc
                })
              ) as MigrationEndpoint[]
            }
          } catch (e) {
            console.error('Failed to parse migration endpoints:', e)
          }
        }
      }
    }
    return []
  }, [tuples])

  // Auto-select all endpoints when they're first loaded (only once)
  useEffect(() => {
    if (migrationEndpoints.length > 0 && !hasInitializedEndpoints) {
      setSelectedEndpoints(migrationEndpoints)
      setHasInitializedEndpoints(true)
    }
  }, [migrationEndpoints, hasInitializedEndpoints])

  // Handler for endpoint selection change
  const handleEndpointSelectionChange = (newSelectedEndpoints: MigrationEndpoint[]) => {
    setSelectedEndpoints(newSelectedEndpoints)
  }

  // Handler for submitting selected endpoints
  const handleEndpointSubmit = async (
    selectedEndpointsForSubmission: MigrationSelectedEndpoint[]
  ) => {
    if (!uuid || !workspaceUuid || !task?.task_id || isSubmittingEndpoints) return

    setIsSubmittingEndpoints(true)
    try {
      await submitMigrationEndpoints({
        workspaceUuid,
        uuid,
        taskId: task.task_id,
        selectedEndpoints: selectedEndpointsForSubmission
      })
      refetchTask()
    } catch (error) {
      console.error('Failed to submit endpoints:', error)
    } finally {
      setIsSubmittingEndpoints(false)
    }
  }

  // Extract question content from the QUESTION_ASKED tuple
  const questionContent = useMemo(() => {
    for (const tuple of tuples) {
      if (tuple.taskStatus === AgentTaskStatus.QUESTION_ASKED) {
        const qaOutput = tuple.response?.[0]?.responseContentStream?.questionAskingOutput
        if (qaOutput?.questionAskingInputs?.length) {
          // Extract text from question blocks
          return qaOutput.questionAskingInputs
            .map(block => {
              if (block.textBlockWithAttachment?.text) return block.textBlockWithAttachment.text
              if (block.dropdownBlock?.blockTitle) return block.dropdownBlock.blockTitle
              return ''
            })
            .filter(Boolean)
            .join('\n\n')
        }
      }
    }
    return ''
  }, [tuples])

  // Handler for submitting question answer
  const handleSubmitQuestionAnswer = async () => {
    if (!task?.task_id || !workspaceUuid || !uuid || isSubmittingAnswer || !questionAnswer.trim())
      return
    setIsSubmittingAnswer(true)
    try {
      await submitQuestionAnswer({
        workspaceUuid,
        uuid,
        taskId: task.task_id,
        answer: questionAnswer.trim()
      })
      refetchTask()
    } catch (error) {
      console.error('Failed to submit question answer:', error)
    } finally {
      setIsSubmittingAnswer(false)
    }
  }

  const tupleStatuses = useMemo(
    () => extractStatusesFromTuples(tuples, isDocumentCommand, commandType),
    [tuples, isDocumentCommand, commandType]
  )

  // Count tuples to detect when feedback is processed or new image is generated
  const feedbackProvidedCount = useMemo(() => {
    return tupleStatuses.filter(t => t.status === AgentTaskStatus.GENERAL_TXT_FEEDBACK_PROVIDED)
      .length
  }, [tupleStatuses])

  const imageGeneratedCount = useMemo(() => {
    return tupleStatuses.filter(t => t.status === AgentTaskStatus.IMAGE_GENERATED).length
  }, [tupleStatuses])

  // Track previous counts to detect changes
  const prevCountsRef = React.useRef({
    feedback: feedbackProvidedCount,
    image: imageGeneratedCount
  })

  // Reset submitting state when feedback is acknowledged or new image is generated
  useEffect(() => {
    const prevCounts = prevCountsRef.current
    const feedbackChanged = feedbackProvidedCount !== prevCounts.feedback
    const imageChanged = imageGeneratedCount !== prevCounts.image

    if (feedbackChanged || imageChanged) {
      setIsSubmittingFeedback(false)
      setImageFeedback('')
      prevCountsRef.current = { feedback: feedbackProvidedCount, image: imageGeneratedCount }
    }
  }, [feedbackProvidedCount, imageGeneratedCount])

  // Handler for submitting image feedback
  const handleSubmitImageFeedback = async () => {
    if (!task?.task_id || !workspaceUuid || !uuid || isSubmittingFeedback) return
    setIsSubmittingFeedback(true)
    try {
      await regenerateImage({
        workspaceUuid,
        uuid,
        taskId: task.task_id,
        comment: imageFeedback
      })
      refetchTask()
    } catch (error) {
      console.error('Failed to regenerate image:', error)
      BetterToast.error('Failed to submit image feedback. Please try again.')
    } finally {
      setIsSubmittingFeedback(false)
    }
  }

  // Handle Enter key to submit feedback
  const handleFeedbackKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmitImageFeedback()
    }
  }

  const steps = useMemo(
    () =>
      groupMessagesIntoStatusSteps(progressMessages, tupleStatuses, isDocumentCommand, commandType),
    [progressMessages, tupleStatuses, isDocumentCommand, commandType]
  )

  // Build lookup map from timeline blocks: index -> { userId, userName, blockIndex, children, status }
  // Timeline blocks have fields.status, fields.user_id, fields.user_name, fields.index, children
  // Key by index to handle multiple blocks with same status (e.g., multiple IMAGE_GENERATED)
  const timelineBlockInfoByIndex = useMemo(() => {
    const map = new Map<
      number,
      {
        userId?: number
        userName?: string
        blockIndex?: number
        children?: unknown[] | null
        status?: AgentTaskStatus
      }
    >()
    for (const block of timelineBlocks) {
      const fields = block.fields
      if (fields?.index !== undefined && typeof fields.index === 'number') {
        const statusEnum =
          fields?.status !== undefined ? agentTaskStatusFromJSON(fields.status) : undefined
        map.set(fields.index, {
          userId: typeof fields.user_id === 'number' ? fields.user_id : undefined,
          userName: typeof fields.user_name === 'string' ? fields.user_name : undefined,
          blockIndex: fields.index,
          children: block.children,
          status: statusEnum
        })
      }
    }
    return map
  }, [timelineBlocks])

  // Helper to check if a status has viewable details
  const hasTaskDetail = (status: AgentTaskStatus) => {
    // The deploy panel renders inline in the timeline at this status, so a
    // separate "View Details" affordance would just duplicate it.
    if (status === AgentTaskStatus.MIGRATION_AWAITING_DEPLOY_INPUT) {
      return false
    }
    return TaskStatuses[status]?.detailType !== TaskStatusDetailType.NONE
  }

  // Handle clicking "See details"
  const onClickDetails = (stepStatus: AgentTaskStatus, stepIndex: number, stepLabel: string) => {
    // For document-generating steps, prefer the in-page redirect when the
    // host view exposes one — keeps the heavy doc rendering inside the
    // dedicated viewer tab rather than spinning it up again inside a modal.
    if (onNavigateToDocument && DOCUMENT_REDIRECT_STATUSES.has(stepStatus)) {
      onNavigateToDocument(stepStatus, stepIndex, stepLabel)
      return
    }
    openModal({ title: stepLabel, taskStatus: stepStatus, index: stepIndex })
  }

  // Collect all IMAGE_GENERATED timeline blocks with their children, keyed by step index
  const imageBlocksByStepIndex = useMemo(() => {
    const result = new Map<number, BlockType[]>()
    // Track which IMAGE_GENERATED blocks we've seen (by their order of appearance)
    let imageGenCount = 0

    for (const block of timelineBlocks) {
      const fields = block.fields
      if (fields?.status !== undefined) {
        const statusEnum = agentTaskStatusFromJSON(fields.status)
        if (statusEnum === AgentTaskStatus.IMAGE_GENERATED) {
          const children = block.children as BlockType[] | undefined
          if (children && children.length > 0) {
            // Use fields.index if available, otherwise use appearance order
            const blockIndex = typeof fields.index === 'number' ? fields.index : imageGenCount
            result.set(blockIndex, children)
          }
          imageGenCount++
        }
      }
    }
    return result
  }, [timelineBlocks])

  // Check if there are unrendered IMAGE_GENERATED blocks (timeline has images not in steps)
  const hasUnrenderedImageBlocks = useMemo(() => {
    const stepImageIndices = new Set(
      steps.filter(s => s.status === AgentTaskStatus.IMAGE_GENERATED).map(s => s.index)
    )
    for (const block of timelineBlocks) {
      const fields = block.fields
      if (fields?.status !== undefined) {
        const statusEnum = agentTaskStatusFromJSON(fields.status)
        if (statusEnum === AgentTaskStatus.IMAGE_GENERATED) {
          const blockIndex = typeof fields.index === 'number' ? fields.index : -1
          if (
            !stepImageIndices.has(blockIndex) &&
            block.children &&
            Array.isArray(block.children) &&
            block.children.length > 0
          ) {
            return true
          }
        }
      }
    }
    return false
  }, [steps, timelineBlocks])

  // Render image content for IMAGE_GENERATED status
  const renderImageContent = (step: TaskProgressStep) => {
    if (step.status !== AgentTaskStatus.IMAGE_GENERATED || !uuid) return null

    // Get children from our precomputed map
    const children = imageBlocksByStepIndex.get(step.index)

    // Render all children (same as TimelineBlock does)
    if (children && children.length > 0) {
      return (
        <div className='w-full space-y-4'>
          {children.map(child => (
            <Block key={child.id} block={child} className='pb-4' _uuid={uuid} />
          ))}
        </div>
      )
    }

    // Fallback: extract images from tuple diffs
    const tuple = tuples[step.index]
    if (!tuple) return null
    const allDiffs = tuple.response?.[0]?.responseContentStream?.diff || []
    const imageDiffs = allDiffs.filter(diff => {
      const filePath = diff.bFilePath || diff.aFilePath || ''
      return (
        filePath.endsWith('.png.b64') ||
        filePath.endsWith('.jpg.b64') ||
        filePath.endsWith('.jpeg.b64') ||
        filePath.endsWith('.svg.b64')
      )
    })

    if (imageDiffs.length === 0) return null

    return (
      <div className='w-full space-y-4'>
        {imageDiffs.map((diff, i) => {
          const content = diff.bFileContent || diff.aFileContent || ''
          if (!content) return null
          return (
            <img
              key={`img-${i}`}
              src={`data:image/png;base64,${content}`}
              alt='Generated image'
              className='max-w-full rounded'
            />
          )
        })}
      </div>
    )
  }

  const isTaskDone = useMemo(
    () => isTaskEffectivelyDone(taskStatus, commandType ?? CommandType.UNRECOGNIZED),
    [taskStatus, commandType]
  )

  // Extract TODO_LIST messages
  // TODO: Re-enable hiding when task is done after styling is complete
  const todoListMessages = useMemo(() => {
    // Temporarily show TODO_LIST for styling
    // if (isTaskDone) return []
    return progressMessages.filter(
      msg => msg.messageType === ProgressMessageType.TODO_LIST && msg.todoList?.length
    )
  }, [progressMessages])

  // Get repo contexts via the shared hook (works for both single and multi-repo)
  const repoContexts = useRepoContexts()

  const taskHeaderInfo = useMemo(() => {
    const displayTaskId = taskId || task?.task_id || task?.id
    if (!displayTaskId) return null

    const cmdType = task?.cmd_type ? commandTypeFromJSON(task.cmd_type) : undefined
    const commandLabel = cmdType && AgentCommands[cmdType as UsedCommandType]?.label

    const getRepoDisplayName = (ctx: {
      repoName?: string
      branchName?: string
      s3TarFilePath?: string
    }) => {
      if (ctx.repoName) {
        let name = extractFileDisplayName(ctx.repoName)
        if (ctx.branchName) name += ` • ${ctx.branchName}`
        return name
      }
      if (ctx.s3TarFilePath) {
        const fileName = ctx.s3TarFilePath.split('/').pop() || ''
        return fileName.replace(/\.tar\.gz$/, '').replace(/^.*___/, '') || null
      }
      return null
    }

    const repoNames = repoContexts.map(ctx => getRepoDisplayName(ctx)).filter(Boolean) as string[]

    let repositoryDisplay: string | null = null
    if (repoNames.length === 1) {
      repositoryDisplay = repoNames[0]
    } else if (repoNames.length > 1) {
      repositoryDisplay = `${repoNames.length} repositories`
    }

    return {
      taskId: displayTaskId,
      commandLabel: commandLabel || task?.cmd_type || 'Task',
      repositoryDisplay,
      repositoryNames: repoNames,
      scheduledTaskId: task?.scheduled_task_id ?? null,
      scheduledTaskUuid: task?.scheduled_task_uuid ?? null
    }
  }, [task, taskId, repoContexts])

  if (steps.length === 0 && todoListMessages.length === 0) return null

  return (
    <div
      className={`flex flex-col items-center self-center flex-1 ${isPlugin ? 'gap-3 pt-2 pb-10 w-full max-w-full px-1' : 'gap-[24px] pt-[16px] pb-[120px] w-[720px] max-w-[720px]'}`}
    >
      {/* Task Header */}
      {taskHeaderInfo && (
        <div
          className={`flex flex-col items-start self-stretch rounded-[24px] bg-[rgba(0,0,0,0.02)] ${isPlugin ? 'py-3 gap-2 w-full px-3' : 'py-[24px] gap-[16px] w-[720px] px-[60px]'}`}
        >
          <div className='font-bold font-sans text-[10px] text-[rgba(0,0,0,0.25)] leading-normal'>
            TASK INFORMATION
          </div>

          <div
            className={`flex self-stretch ${isPlugin ? 'flex-col gap-3' : 'items-center gap-[48px]'}`}
          >
            <TaskHeaderField
              label='Task'
              value={`Task #${taskHeaderInfo.taskId} - ${taskHeaderInfo.commandLabel}`}
            />
            <TaskHeaderField
              label='Repository'
              allowOverflow={taskHeaderInfo.repositoryNames.length > 1}
              value={
                taskHeaderInfo.repositoryNames.length > 1 ? (
                  <RepositoryListDisplay names={taskHeaderInfo.repositoryNames} />
                ) : (
                  taskHeaderInfo.repositoryDisplay || 'No Repository'
                )
              }
            />
            {taskHeaderInfo.scheduledTaskId ? (
              <TaskHeaderField
                label='Scheduled by'
                value={
                  <ScheduleUuidBadge
                    uuid={taskHeaderInfo.scheduledTaskUuid}
                    fallbackId={taskHeaderInfo.scheduledTaskId}
                  />
                }
              />
            ) : null}
          </div>
        </div>
      )}

      <div
        className={`flex flex-col self-stretch ${isPlugin ? 'gap-2 px-0' : 'gap-[24px] px-[12px]'}`}
      >
        {steps.map((step, idx) => {
          // Step is only "last" if it's the final step AND there are no unrendered image blocks after
          const isLast = idx === steps.length - 1 && !hasUnrenderedImageBlocks
          const iconKind: 'done' | 'active' =
            !(idx === steps.length - 1) || isTaskDone ? 'done' : 'active'
          // Default to isExpanded (true) when key not set
          const expandedRequested = expandedKeys[step.key] ?? true

          const expandable = step.messages.length > 0
          const isExpanded = expandable && expandedRequested

          const blockInfo = timelineBlockInfoByIndex.get(step.index)

          // Check if this step will render an action button below the status row
          const hasNavigateAction =
            isLast &&
            !!onNavigateToReviewCode &&
            (step.status === AgentTaskStatus.DIFF_GENERATED ||
              step.status === AgentTaskStatus.COMPLETED ||
              step.status === AgentTaskStatus.MIGRATION_CODE_GENERATED)
          const hasDetailAction =
            !hasNavigateAction &&
            hasTaskDetail(step.status) &&
            step.status !== AgentTaskStatus.PLANNING_COMPLETED &&
            step.status !== AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED &&
            step.status !== AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED &&
            step.status !== AgentTaskStatus.REFACTOR_API_SPECS_GENERATED &&
            !(isDocumentCommand && step.status === AgentTaskStatus.DIFF_GENERATED)

          const hasActionButton = hasNavigateAction || hasDetailAction
          const hasVisualContent =
            step.status === AgentTaskStatus.IMAGE_GENERATED ||
            step.status === AgentTaskStatus.DOCUMENT_GENERATED ||
            (isLast && hasActionButton)
          const showUserAttribution =
            !!blockInfo?.userId && !!blockInfo?.userName && blockInfo.userName !== 'Campaign'

          return (
            <div
              key={step.key}
              className={`flex items-start self-stretch relative ${isPlugin ? 'gap-[4px]' : 'gap-[8px]'}`}
            >
              {/* Vertical connecting line - from icon down through gap to next step */}
              {!isLast && !hasVisualContent && (
                <TimelineConnector bottom='-16px' solidColor='bg-[#E8E8ED]' />
              )}

              {/* Fading line for steps with visual content (images/documents) */}
              {hasVisualContent && (
                <TimelineConnector
                  bottom='20px'
                  background='linear-gradient(to bottom, #E8E8ED 0%, #E8E8ED 70%, transparent 100%)'
                />
              )}

              {/* Status Icon */}
              <div className='flex items-center justify-center w-[24px] h-[36px] py-[10px] gap-[10px] shrink-0 relative z-10'>
                <StatusIcon kind={iconKind} />
              </div>

              <div
                className='flex flex-col items-start self-stretch gap-[8px] flex-1 min-w-0'
                style={isPlugin ? undefined : { maxWidth: 'calc(720px - 24px - 8px)' }}
              >
                {/* Header - use explicit width to prevent document from affecting Info button position */}
                <div
                  className={`flex flex-col ${isPlugin ? 'w-full' : 'w-[calc(720px-24px-12px)]'} ${isExpanded ? '' : 'pb-[8px]'} gap-[2px]`}
                >
                  {/* Primary row: status + metadata (timestamp/user) */}
                  <div className='flex items-center w-full'>
                    {/* Left cluster: status label + timestamp + user attribution */}
                    <div
                      className={`flex items-baseline py-[8px] min-w-0 gap-2 flex-wrap ${isPlugin ? 'px-[4px]' : 'px-[16px]'}`}
                    >
                      {/* Status label (+ optional chevron when expandable) */}
                      <StatusLabelRow
                        label={step.statusLabel}
                        expandable={expandable}
                        isExpanded={isExpanded}
                        onToggle={() =>
                          setExpandedKeys(prev => ({ ...prev, [step.key]: !isExpanded }))
                        }
                        feedbackText={step.feedbackText}
                        fullWidth={false}
                      />

                      {/* Timestamp metadata block (clock icon + formatted time) */}
                      {step.timestamp && (
                        <div className='flex items-baseline gap-[4px]'>
                          <ClockMetaIcon />
                          <span className='font-sans text-[10px] font-bold leading-normal text-[rgba(0,0,0,0.25)] whitespace-normal'>
                            {dayjs(step.timestamp).format('h:mm:ss A, MMM DD, YYYY')}
                          </span>
                        </div>
                      )}

                      {/* User attribution metadata block (contact icon + popover) */}
                      {showUserAttribution && blockInfo?.userName && blockInfo?.userId && (
                        <UserPopover
                          userName={blockInfo.userName}
                          userId={blockInfo.userId}
                          showContactIcon
                          triggerClassName='flex items-baseline gap-[4px] py-[4px] px-[8px] bg-[#F7F7FF] rounded-[50px] text-[#5B5BD5] text-[10px] font-bold min-w-0'
                          iconClassName='self-center'
                          nameClassName='text-[11px] font-medium leading-normal'
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* TODO_LIST - show in the step matching the todo list's taskStatus */}
                {todoListMessages
                  .filter(todoMsg => todoMsg.taskStatus === step.statusValue)
                  .map(
                    todoMsg =>
                      todoMsg.todoList &&
                      isExpanded && (
                        <TodoList
                          key={todoMsg.msgHash}
                          items={todoMsg.todoList}
                          forceComplete={isTaskDone}
                        />
                      )
                  )}

                {/* Inline Endpoint Selector for MIGRATION_ENDPOINTS_IDENTIFIED */}
                {isLast &&
                  step.status === AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED &&
                  taskStatus === AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED &&
                  migrationEndpoints.length > 0 && (
                    <div className={`py-[8px] w-full ${isPlugin ? 'px-[4px]' : 'px-[16px]'}`}>
                      <MigrationEndpointSelector
                        endpoints={migrationEndpoints}
                        selectedEndpoints={selectedEndpoints}
                        onSelectionChange={handleEndpointSelectionChange}
                        onSubmit={endpoints => handleEndpointSubmit(endpoints)}
                        isSubmitting={isSubmittingEndpoints}
                        showSubmitButton={true}
                        inline={true}
                      />
                    </div>
                  )}

                {/* Inline Azure deploy panel for MIGRATION_AWAITING_DEPLOY_INPUT */}
                {isLast &&
                  step.status === AgentTaskStatus.MIGRATION_AWAITING_DEPLOY_INPUT &&
                  taskStatus === AgentTaskStatus.MIGRATION_AWAITING_DEPLOY_INPUT &&
                  taskId != null &&
                  workspaceUuid &&
                  uuid && (
                    <div className={`py-[8px] w-full ${isPlugin ? 'px-[4px]' : 'px-[16px]'}`}>
                      <AzureDeployTaskModal
                        tuples={tuples}
                        index={0}
                        taskId={taskId}
                        workspaceUuid={workspaceUuid}
                        uuid={uuid}
                        currentTaskStatus={taskStatus}
                      />
                    </div>
                  )}

                {/* Inline Question + Answer for QUESTION_ASKED */}
                {isLast &&
                  step.status === AgentTaskStatus.QUESTION_ASKED &&
                  taskStatus === AgentTaskStatus.QUESTION_ASKED && (
                    <div
                      className={`py-[8px] w-full flex flex-col gap-3 ${isPlugin ? 'px-[4px]' : 'px-[16px]'}`}
                    >
                      {questionContent && (
                        <div className='rounded-[12px] bg-[rgba(0,0,0,0.03)] px-4 py-3'>
                          <ProgressMarkdown text={questionContent} />
                        </div>
                      )}
                      {isSubmittingAnswer ? (
                        <div className='flex items-center gap-2 text-[14px] text-[rgba(0,0,0,0.45)]'>
                          <ProgressSpinner size='sm' />
                          <span>Submitting answer...</span>
                        </div>
                      ) : (
                        <div className='flex items-center justify-between gap-x-2'>
                          <Input
                            value={questionAnswer}
                            onChange={e => setQuestionAnswer(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault()
                                handleSubmitQuestionAnswer()
                              }
                            }}
                            placeholder='Type your answer...'
                            className='text-[14px] px-[16px]'
                          />
                          <Button
                            variant='borderless'
                            isDisabled={!questionAnswer.trim()}
                            onClick={handleSubmitQuestionAnswer}
                          >
                            Submit
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                {/* Content */}
                {isExpanded && (
                  <div
                    className={`flex flex-col pb-[8px] gap-[8px] items-start self-stretch w-full whitespace-pre-wrap ${isPlugin ? 'px-[4px]' : 'px-[16px]'}`}
                  >
                    {step.messages.map((msg, msgIndex) => {
                      const internalMsgs = (msg.internalMsgs ?? []).filter(Boolean)
                      const toolOutputs = (msg.toolOutputs ?? []).filter(Boolean)
                      const hasDetails =
                        internalMsgs.length > 0 ||
                        toolOutputs.length > 0 ||
                        (msg.messageType === ProgressMessageType.CHECKPOINT && !!msg.checkpointMsg)
                      const isLastMessage = msgIndex === step.messages.length - 1

                      return (
                        <div key={msg.msgHash} className=''>
                          {/* Message title */}
                          {msg.title ? (
                            <div className='text-[12px] font-sans font-medium leading-normal self-stretch text-[rgba(0,0,0,0.45)]'>
                              <ProgressMarkdown text={msg.title} />
                            </div>
                          ) : null}

                          {/* Details */}
                          {hasDetails ? (
                            <div
                              className={`flex flex-col gap-[8px] ${isLastMessage ? '' : 'pb-[8px]'}`}
                            >
                              {msg.messageType === ProgressMessageType.CHECKPOINT &&
                              msg.checkpointMsg ? (
                                <CheckpointCard text={msg.checkpointMsg} />
                              ) : null}

                              {internalMsgs.length > 0 ? (
                                <div className='flex flex-col'>
                                  {internalMsgs.map((line, i) => (
                                    <div
                                      key={`${msg.msgHash}-internal-${i}`}
                                      className='text-[12px] leading-[16px]'
                                    >
                                      {isFileName(line) ? (
                                        <div className='flex items-center gap-[6px] px-2 py-1 bg-[rgba(0,0,0,0.03)] rounded-md w-fit'>
                                          <svg
                                            width='14'
                                            height='14'
                                            viewBox='0 0 16 16'
                                            fill='none'
                                            className='flex-shrink-0'
                                          >
                                            <path
                                              d='M13.5 4.5V13.5C13.5 13.7652 13.3946 14.0196 13.2071 14.2071C13.0196 14.3946 12.7652 14.5 12.5 14.5H3.5C3.23478 14.5 2.98043 14.3946 2.79289 14.2071C2.60536 14.0196 2.5 13.7652 2.5 13.5V2.5C2.5 2.23478 2.60536 1.98043 2.79289 1.79289C2.98043 1.60536 3.23478 1.5 3.5 1.5H9.5L13.5 4.5Z'
                                              stroke='#8c8c8c'
                                              strokeLinecap='round'
                                              strokeLinejoin='round'
                                            />
                                            <path
                                              d='M9.5 1.5V4.5H13.5'
                                              stroke='#8c8c8c'
                                              strokeLinecap='round'
                                              strokeLinejoin='round'
                                            />
                                          </svg>
                                          <span className='font-mono font-semibold text-[10px] leading-normal text-[rgba(0,0,0,0.65)]'>
                                            {line.trim()}
                                          </span>
                                        </div>
                                      ) : (
                                        <span className='font-sans font-bold text-[10px] leading-normal text-[rgba(0,0,0,0.25)]'>
                                          <ProgressMarkdown text={line} />
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : null}

                              {toolOutputs.length > 0 ? (
                                <div className='flex flex-col gap-[8px]'>
                                  {toolOutputs.map((out, i) => (
                                    <pre
                                      key={`${msg.msgHash}-tool-${i}`}
                                      className='font-mono text-[11px] leading-[16px] whitespace-pre-wrap text-[rgba(0,0,0,0.55)]'
                                    >
                                      {out}
                                    </pre>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Document/DataWeave now shown via detail button instead of inline */}

                {/* Image content for IMAGE_GENERATED status */}
                {renderImageContent(step)}

                {/* Image feedback + regenerate for IMAGE_GENERATED status (last step only, and only if no unrendered images) */}
                {!isAdminTask &&
                  step.status === AgentTaskStatus.IMAGE_GENERATED &&
                  idx === steps.length - 1 &&
                  !hasUnrenderedImageBlocks && (
                    <div className={`w-full ${isPlugin ? 'px-[4px]' : 'px-[16px]'}`}>
                      {isSubmittingFeedback ? (
                        <div className='flex items-center gap-2 text-[14px] text-[rgba(0,0,0,0.45)]'>
                          <ProgressSpinner size='sm' />
                          <span>Regenerating image...</span>
                        </div>
                      ) : (
                        <div className='flex items-center justify-between gap-x-2'>
                          <Input
                            value={imageFeedback}
                            onChange={e => setImageFeedback(e.target.value)}
                            onKeyDown={handleFeedbackKeyDown}
                            placeholder='Provide feedback for the image'
                            className='text-[14px] px-[16px]'
                          />
                          <Button
                            variant='primary'
                            onClick={handleSubmitImageFeedback}
                            isDisabled={!task?.task_id || !workspaceUuid || !uuid}
                          >
                            Regenerate
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                {/* STYLE HOOK: inline action buttons — rendered after all content */}
                {(() => {
                  const showDetailAction = hasDetailAction

                  if (!showDetailAction && !hasNavigateAction) return null

                  return (
                    <div
                      className={`flex items-center gap-2 pb-[8px] ${isPlugin ? 'px-[4px]' : 'px-[16px]'}`}
                    >
                      {showDetailAction &&
                        (() => {
                          const blockIndex = blockInfo?.blockIndex ?? step.index
                          // When the click is wired to redirect into a viewer
                          // tab (rather than open a modal), use the prominent
                          // navigate-style CTA so the affordance matches the
                          // actual behavior.
                          const willRedirect =
                            !!onNavigateToDocument && DOCUMENT_REDIRECT_STATUSES.has(step.status)
                          return (
                            <InlineActionButton
                              variant={willRedirect ? 'navigate' : 'detail'}
                              label={getActionLabel(step.status, isDocumentCommand, commandType)}
                              onClick={() =>
                                onClickDetails(
                                  step.status,
                                  Math.max(0, blockIndex),
                                  step.statusLabel
                                )
                              }
                              stopPropagation={expandable}
                            />
                          )
                        })()}

                      {hasNavigateAction &&
                        (() => {
                          // Label must match the tab we navigate to. Migration
                          // Accelerator is a document-generating command, but its
                          // diffs open in the 'review-code' tab rather than a
                          // DocumentList, so the
                          // accurate verb is "Review Code", not "View Result".
                          const isMigrationAccelerator =
                            commandType === CommandType.MIGRATION_ACCELERATOR
                          const label =
                            isMigrationAccelerator ||
                            (step.status === AgentTaskStatus.DIFF_GENERATED && !isDocumentCommand)
                              ? 'Review Code'
                              : 'View Results'
                          return (
                            <InlineActionButton
                              variant='navigate'
                              label={label}
                              onClick={onNavigateToReviewCode!}
                            />
                          )
                        })()}
                    </div>
                  )
                })()}
              </div>
            </div>
          )
        })}

        {/* Render any IMAGE_GENERATED blocks from timeline that weren't matched to a step */}
        {/* This handles the case where timeline blocks are updated before tuples */}
        {(() => {
          // Get all step indices that have IMAGE_GENERATED status
          const renderedImageIndices = new Set(
            steps.filter(s => s.status === AgentTaskStatus.IMAGE_GENERATED).map(s => s.index)
          )

          // Find IMAGE_GENERATED timeline blocks that weren't rendered (with their metadata)
          const unrenderedImageBlocks: Array<{
            children: BlockType[]
            timestamp?: number
            userName?: string
            userId?: number
          }> = []
          for (const block of timelineBlocks) {
            const fields = block.fields
            if (fields?.status !== undefined) {
              const statusEnum = agentTaskStatusFromJSON(fields.status)
              if (statusEnum === AgentTaskStatus.IMAGE_GENERATED) {
                const blockIndex = typeof fields.index === 'number' ? fields.index : -1
                if (
                  !renderedImageIndices.has(blockIndex) &&
                  block.children &&
                  Array.isArray(block.children) &&
                  block.children.length > 0
                ) {
                  unrenderedImageBlocks.push({
                    children: block.children as BlockType[],
                    timestamp: typeof fields.ts === 'number' ? fields.ts : undefined,
                    userName: typeof fields.user_name === 'string' ? fields.user_name : undefined,
                    userId: typeof fields.user_id === 'number' ? fields.user_id : undefined
                  })
                }
              }
            }
          }

          if (unrenderedImageBlocks.length === 0 || !uuid) return null

          // These unrendered blocks are the latest images, so show feedback input after the last one
          const showFeedbackForUnrendered = !isAdminTask

          return unrenderedImageBlocks.map((imageBlock, blockIdx) => {
            const isLastUnrendered = blockIdx === unrenderedImageBlocks.length - 1

            return (
              <div
                key={`unrendered-image-${blockIdx}`}
                className={`flex items-start self-stretch relative ${isPlugin ? 'gap-[4px]' : 'gap-[8px]'}`}
              >
                {/* Connecting line from previous step */}
                <div
                  className='absolute left-[9px] w-[2px] bg-[#E8E8ED]'
                  style={{ zIndex: 0, top: '-16px', height: '44px' }}
                />

                {/* Fading line for image content */}
                <div
                  className='absolute left-[9px] top-[28px] w-[2px]'
                  style={{
                    zIndex: 0,
                    bottom: '20px',
                    background:
                      'linear-gradient(to bottom, #E8E8ED 0%, #E8E8ED 70%, transparent 100%)'
                  }}
                />

                {/* Status Icon */}
                <div className='flex items-center w-[24px] h-[36px] gap-[10px] py-[10px] relative z-10'>
                  <StatusIcon kind={isTaskDone ? 'done' : 'active'} />
                </div>

                <div
                  className='flex flex-col items-start self-stretch gap-[8px] flex-1 min-w-0'
                  style={isPlugin ? undefined : { maxWidth: 'calc(720px - 24px - 8px)' }}
                >
                  {/* Header */}
                  <div
                    className={`flex flex-col ${isPlugin ? 'w-full px-[4px]' : 'w-[calc(720px-24px-12px)] px-[16px]'} py-[8px] gap-[2px]`}
                  >
                    <div className='flex items-center gap-1 text-[11px] text-[rgba(0,0,0,0.45)] leading-normal'>
                      {imageBlock.timestamp !== undefined && imageBlock.timestamp !== 0 && (
                        <span>{dayjs(imageBlock.timestamp).format('h:mm:ss A, MMM DD, YYYY')}</span>
                      )}
                      {imageBlock.userId !== undefined &&
                        imageBlock.userId > 0 &&
                        imageBlock.userName &&
                        imageBlock.userName !== 'Campaign' && (
                          <>
                            <span>by</span>
                            <UserPopover
                              userName={imageBlock.userName}
                              userId={imageBlock.userId}
                            />
                          </>
                        )}
                    </div>
                    <div className='text-[16px] font-medium leading-[24px] text-[rgba(91,91,213,1)]'>
                      Image Generated
                    </div>
                  </div>

                  {/* Image content */}
                  <div className='w-full space-y-4 overflow-x-auto'>
                    {imageBlock.children.map(child => (
                      <Block
                        key={child.id}
                        block={child}
                        className='pb-4 max-w-full [&_img]:max-w-full [&_img]:h-auto'
                        _uuid={uuid}
                      />
                    ))}
                  </div>

                  {/* Show feedback input for the last unrendered image */}
                  {isLastUnrendered && showFeedbackForUnrendered && (
                    <div className={`w-full ${isPlugin ? 'px-[4px]' : 'px-[16px]'}`}>
                      {isSubmittingFeedback ? (
                        <div className='flex items-center gap-2 text-[14px] text-[rgba(0,0,0,0.45)]'>
                          <ProgressSpinner size='sm' />
                          <span>Regenerating image...</span>
                        </div>
                      ) : (
                        <div className='flex items-center justify-between gap-x-2'>
                          <Input
                            value={imageFeedback}
                            onChange={e => setImageFeedback(e.target.value)}
                            onKeyDown={handleFeedbackKeyDown}
                            placeholder='Provide feedback for the image'
                          />
                          <Button
                            variant='primary'
                            onClick={handleSubmitImageFeedback}
                            isDisabled={!task?.task_id || !workspaceUuid || !uuid}
                          >
                            Regenerate
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })
        })()}
      </div>
    </div>
  )
}

export default TaskProgressMessageView
